import { Component, EventEmitter, Inject, Input, OnInit, Optional, Output } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { IcdSuggestionService } from 'src/app/core/services/icd-suggestion.service';
import {
  buildDecisions as buildDecisionsPure,
  buildCodingSet as buildCodingSetPure,
  bulkAcceptable as bulkAcceptablePure,
  primaryWarning as primaryWarningPure,
} from './icd-coding-set-view';

/**
 * Review surface for ICD-10 coding suggestions (tasks/icd10-suggestion-pipeline.md §11).
 *
 * Two things about this screen are not cosmetic:
 *
 *  - **Rejecting is permanent until new evidence** (§7.1). The dialog says so before asking, and
 *    reports honestly afterwards: the server returns `suppressed`, and when it is false the code
 *    CAN be proposed again, so the confirmation must not claim otherwise.
 *  - **Removals are never auto-applied** (§7 rule 6), so they are listed first — they are the
 *    ones that always need a human, whatever the practice's auto-accept setting is.
 */
@Component({
  selector: 'app-icd-suggestions-dialog',
  templateUrl: './icd-suggestions-dialog.component.html',
  styleUrls: ['./icd-suggestions-dialog.component.scss']
})
export class IcdSuggestionsDialogComponent implements OnInit {

  /**
   * Rendered inside another panel rather than as a dialog of its own.
   *
   * The billing review wizard supplies its own header, its own footer and its own scrolling
   * region, so this component's `mat-dialog-title`, `mat-dialog-actions` and the `max-height`
   * on its content are all wrong there: they duplicate the wizard's title, add a Close button
   * with no dialog to close (`dialogRef` is null when embedded), and produce a SECOND scrollbar
   * inside the wizard's own.
   */
  @Input() embedded = false;

  /**
   * Fires with the patient id when a decision made HERE has just emptied this patient's queue.
   *
   * ⚠ Only after a decision, never on arrival. A queue that was already empty when the surface
   * was pointed at the patient does not fire: the billing review wizard uses this to move on to
   * the next patient automatically, and a biller who has deliberately stepped back to a cleared
   * patient must not be bounced off them. `changed` cannot serve here because it accumulates
   * across the whole run, so a fresh per-patient flag is kept instead.
   */
  @Output() queueCleared = new EventEmitter<string>();

  /** Whether any decision has been resolved since `setPatient()` — see `queueCleared`. */
  private resolvedForCurrentPatient = false;

  patientId: string;
  patientName: string;

  suggestions: any[] = [];
  counts: any = { total: 0 };
  loading = true;
  loadError: string | null = null;

  /** The codes already on the record, from the server. The left-hand side of the diff. */
  currentCodes: any[] = [];

  /**
   * The resulting coding SET, as a diff — what the record would look like if everything pending
   * were accepted. ICD-10 rules constrain the set, not the individual edits, so this is the view
   * the rules are actually about.
   */
  codingSet: any[] = [];

  /**
   * The decision list. Coupled suggestions collapse into ONE entry here, because they are one
   * decision: accepting a dagger without its asterisk is an invalid record, and the server now
   * refuses to let that happen. Offering two buttons for something that resolves as a unit would
   * misrepresent what the click does.
   */
  decisions: any[] = [];

  /** `decisions` split for rendering: coupled groups read as one card, singles as the old ones. */
  coupledDecisions: any[] = [];
  singleDecisions: any[] = [];

  /** Ids of decisions being resolved by `Accept all`, so the button can report progress. */
  bulkRunning = false;
  bulkMessage: string | null = null;

  /** Suggestion id currently showing its reject form, and the reason typed into it. */
  rejectingId: string | null = null;
  rejectReason = '';

  /** Suggestion ids with an in-flight request, so a card cannot be double-submitted. */
  busy = new Set<string>();

  /** Set once anything is accepted or rejected, so the caller knows to refetch the patient. */
  changed = false;

  /** Per-code audit history, keyed by code, loaded on demand. */
  auditByCode: { [code: string]: any[] } = {};
  auditLoading: string | null = null;

  /**
   * History-only mode. Set when opened from an AI-applied or flagged chip: the dialog then shows
   * just that code's audit trail and no review queue. One component renders audit entries, rather
   * than two that can drift apart.
   */
  historyOnlyCode: string | null = null;

  constructor(
    private icdSuggestionService: IcdSuggestionService,
    // ⚠ BOTH OPTIONAL, so this component can be EMBEDDED as well as opened as a dialog.
    //
    // The billing review wizard renders it inside its own panel and drives it with
    // `setPatient()`. Outside a dialog neither `MatDialogRef` nor `MAT_DIALOG_DATA` exists, and
    // a required injection would fail at construction — so the alternative was a second copy of
    // this review surface, which carries the §11.13 coupling rules, the coding-set diff and the
    // §7 rule 6 bulk guard. Three safety rules are not worth duplicating to avoid two decorators.
    @Optional() private dialogRef: MatDialogRef<IcdSuggestionsDialogComponent> | null,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.patientId = data?.patientId;
    this.patientName = data?.patientName || 'this patient';
    this.historyOnlyCode = data?.code || null;
  }

  ngOnInit(): void {
    // Embedded (no dialog data): the host drives this with `setPatient()`, so there is nothing to
    // load yet. Without this guard the wizard's instance would fire a request for `undefined` on
    // construction and render its own error banner before the first patient is even chosen.
    if (!this.patientId) {
      this.loading = false;
      return;
    }
    if (this.historyOnlyCode) {
      this.loading = false;
      this.toggleAudit(this.historyOnlyCode);
      return;
    }
    this.load();
  }

  /**
   * Points this dialog at a different patient and reloads.
   *
   * Exists for the billing review wizard, which walks a set of patients through ONE dialog
   * instance rather than opening and closing one per patient — a dialog the user has dragged and
   * resized must not jump back to the middle every time they press Next.
   *
   * ⚠ EVERY PIECE OF PER-PATIENT STATE MUST BE RESET HERE. The queue, the coding-set diff, the
   * decision groups, the audit cache, the in-flight set and any half-typed rejection all belong
   * to the patient being shown; carrying one over would attribute the previous patient's coding
   * to this one, which on this screen is a clinical error rather than a cosmetic one. `changed`
   * is the deliberate exception — it accumulates across the whole wizard, because the caller
   * needs to know whether ANY patient was edited.
   */
  setPatient(patientId: string, patientName?: string): void {
    this.patientId = patientId;
    this.patientName = patientName || 'this patient';

    this.suggestions = [];
    this.decisions = [];
    this.coupledDecisions = [];
    this.singleDecisions = [];
    this.codingSet = [];
    this.currentCodes = [];
    this.auditByCode = {};
    this.auditLoading = null;
    this.rejectingId = null;
    this.rejectReason = '';
    this.busy = new Set<string>();
    this.bulkRunning = false;
    this.bulkMessage = null;
    this.loadError = null;
    this.historyOnlyCode = null;
    this.resolvedForCurrentPatient = false;

    this.load();
  }

  load(): void {
    this.loading = true;
    this.loadError = null;
    this.icdSuggestionService.getForPatient(this.patientId).subscribe(
      (response) => {
        this.suggestions = this.collapseDuplicates(response?.data || []);
        this.currentCodes = response?.currentCodes || [];
        this.decisions = this.buildDecisions(this.suggestions);
        this.coupledDecisions = this.decisions.filter((d) => d.kind !== 'single');
        this.singleDecisions = this.decisions.filter((d) => d.kind === 'single');
        this.codingSet = this.buildCodingSet(this.currentCodes, this.suggestions);
        this.recount();
        this.loading = false;
        if (this.resolvedForCurrentPatient && this.decisions.length === 0) {
          this.queueCleared.emit(this.patientId);
        }
      },
      (error) => {
        console.error('Error loading ICD suggestions:', error);
        this.loadError = 'The coding suggestions could not be loaded. Please try again.';
        this.loading = false;
      }
    );
  }

  /**
   * Collapses suggestions that propose THE SAME THING onto one card.
   *
   * ⚠ THIS IS A PRESENTATION FIX FOR A PIPELINE DEFECT, and should not outlive it. Episode
   * derivation currently emits several episodes covering the same clinical event — on live data,
   * 42 of 145 episodes shared a patient AND an evidence hash, differing only in `type`
   * (`procedure` / `outpatient` / `chronic`). Each is reviewed independently, so each produces
   * an identical suggestion, and a clinician would be asked to make the same decision three
   * times. That is not a queue anyone will work through.
   *
   * Keyed on what the clinician is actually deciding: the action, the code, and for a replace,
   * which code it displaces. The underlying suggestion ids are all kept, because resolving the
   * card has to resolve every one of them — leaving two pending after accepting the third is
   * exactly the inconsistency that made the removal badges wrong.
   */
  private collapseDuplicates(rows: any[]): any[] {
    const byKey = new Map<string, any>();

    for (const row of rows) {
      const key = `${row.action}|${row.code}|${row.replacesCodeId || ''}`;
      const existing = byKey.get(key);

      if (!existing) {
        byKey.set(key, {
          ...row,
          message: null,
          ids: [row.id],
          episodes: row.episode ? [row.episode] : [],
        });
        continue;
      }

      existing.ids.push(row.id);
      if (row.episode && !existing.episodes.some((e: any) => e.id === row.episode.id)) {
        existing.episodes.push(row.episode);
      }
      // Keep the strongest case: the clinician should judge this on its best evidence, and the
      // confidence shown must match the quote shown.
      if ((row.confidence || 0) > (existing.confidence || 0)) {
        existing.confidence = row.confidence;
        existing.rationale = row.rationale;
        existing.evidence = row.evidence;
        existing.verifierVerdict = row.verifierVerdict;
        existing.verifierRationale = row.verifierRationale;
      }
    }

    return [...byKey.values()];
  }

  // ── Presentation logic lives in ./icd-coding-set-view ────────────────────────────
  //
  // Pure and dependency-free, so it can be exercised directly. These decide what a clinician is
  // OFFERED — which codes are coupled, what the resulting set looks like, what `Accept all`
  // takes — and two of those are safety rules rather than layout. The module was extracted for
  // exactly that reason and then left unused while the component kept its own copy; one copy now.

  private buildDecisions(cards: any[]): any[] {
    return buildDecisionsPure(cards);
  }

  private buildCodingSet(currentCodes: any[], cards: any[]): any[] {
    return buildCodingSetPure(currentCodes, cards);
  }

  /**
   * What to say about the primary diagnosis, or null to say nothing.
   *
   * Null is the COMMON case on live data: `codeRole` has only just started being captured, so
   * 111,610 of 111,611 active codes carry none and "is there a primary?" is unanswerable. The
   * banner used to fire on a zero count alone and was therefore wrong on nearly every patient.
   */
  primaryWarning(): string | null {
    return primaryWarningPure(this.codingSet);
  }

  bulkAcceptable(): any[] {
    return bulkAcceptablePure(this.decisions);
  }

  private recount(): void {
    this.counts = this.suggestions.reduce(
      (acc: any, s: any) => ({ ...acc, [s.action]: (acc[s.action] || 0) + 1 }),
      { total: this.suggestions.length }
    );
  }

  isBusy(id: string): boolean {
    return this.busy.has(id);
  }

  /**
   * Resolves every suggestion id behind a card, one at a time.
   *
   * Sequential, not parallel: accepting two duplicates of the same code concurrently races the
   * service's claim-before-write lock, and one would simply fail. Sequential means the first
   * wins and the rest report "already resolved", which is the correct outcome.
   *
   * A 409 on a LATER id is success, not failure — it means that duplicate was already dealt
   * with. Only a real error is worth telling the clinician about.
   */
  private resolveAll(
    ids: string[],
    call: (id: string) => any,
    onDone: (failed: number, lastResponse: any) => void
  ): void {
    let index = 0;
    let failed = 0;
    let lastResponse: any = null;

    const next = () => {
      if (index >= ids.length) { onDone(failed, lastResponse); return; }
      const id = ids[index++];
      call(id).subscribe(
        (response: any) => { lastResponse = response; next(); },
        (error: any) => {
          if (error?.status !== 409) failed += 1;
          next();
        }
      );
    };
    next();
  }

  /**
   * Accepts a decision.
   *
   * `chosen` names one arm of a choice; without it the whole decision is taken, which for a pair
   * means both halves. The ids of every member are sent even for a pair the server will couple
   * anyway — a member may have duplicates from an earlier run in a DIFFERENT group, and those
   * are not siblings the server can see from the one it was handed.
   */
  acceptDecision(decision: any, chosen: any = null): void {
    if (this.busy.has(decision.key)) return;
    this.busy.add(decision.key);

    const members = chosen ? [chosen] : decision.members;
    const ids = members.reduce((acc: string[], m: any) => acc.concat(m.ids), []);

    this.resolveAll(
      ids,
      (id) => this.icdSuggestionService.acceptSuggestion(this.patientId, id),
      (failed) => {
        this.busy.delete(decision.key);
        this.changed = true;
        this.resolvedForCurrentPatient = true;
        if (failed > 0) {
          decision.message = 'Some of these could not be accepted. Refreshing.';
        }
        // Reload rather than just dropping the card. Accepting a code can invalidate OTHER
        // pending cards — a `replace D43.0 → C71.2` and a separate `add C71.2` can both be
        // waiting, and once either is accepted the other would create a duplicate live code.
        // The server now refuses that, but the clinician should not be left looking at a
        // suggestion that has quietly become impossible. It also settles the coupled siblings
        // the server resolved on its own, which this component never asked about.
        this.load();
      }
    );
  }

  startReject(decision: any): void {
    this.rejectingId = decision.key;
    this.rejectReason = '';
  }

  cancelReject(): void {
    this.rejectingId = null;
    this.rejectReason = '';
  }

  confirmReject(decision: any): void {
    if (this.busy.has(decision.key)) return;
    this.busy.add(decision.key);
    const reason = this.rejectReason;

    this.resolveAll(
      decision.ids,
      (id) => this.icdSuggestionService.rejectSuggestion(this.patientId, id, reason),
      (failed, lastResponse) => {
        this.busy.delete(decision.key);
        this.changed = true;
        this.resolvedForCurrentPatient = true;
        this.rejectingId = null;
        this.rejectReason = '';

        if (failed > 0) {
          decision.message = 'Some of these could not be dismissed. Refreshing.';
          this.load();
          return;
        }

        // Honest about what actually happened. `suppressed: false` means the pipeline may
        // propose this code again — claiming permanence would be a lie the audit trail exposes.
        if (lastResponse?.data?.suppressed === false) {
          decision.message = 'Dismissed, but it may be suggested again on a future run.';
          setTimeout(() => this.load(), 4000);
        } else {
          this.load();
        }
      }
    );
  }

  /**
   * Accepts every decision that is safe to take in one click — see `bulkAcceptable`.
   *
   * Sequential, and it reloads once at the end rather than after each. Accepting one code can
   * make another impossible, and the server is the thing that knows: a failure here is reported,
   * not hidden behind a refresh that makes the card quietly disappear.
   */
  acceptAll(): void {
    const batch = this.bulkAcceptable();
    if (!batch.length || this.bulkRunning) return;

    this.bulkRunning = true;
    this.bulkMessage = null;
    const ids = batch.reduce((acc: string[], d: any) => acc.concat(d.ids), []);

    this.resolveAll(
      ids,
      (id) => this.icdSuggestionService.acceptSuggestion(this.patientId, id),
      (failed) => {
        this.bulkRunning = false;
        this.changed = true;
        this.resolvedForCurrentPatient = true;
        this.bulkMessage = failed > 0
          ? `${failed} of these could not be accepted and are still listed below.`
          : null;
        this.load();
      }
    );
  }

  toggleAudit(code: string): void {
    if (this.auditByCode[code]) {
      delete this.auditByCode[code];
      return;
    }
    this.auditLoading = code;
    this.icdSuggestionService.getAudit(this.patientId, code).subscribe(
      (response) => {
        this.auditByCode[code] = response?.data || [];
        this.auditLoading = null;
      },
      (error) => {
        console.error('Error loading the coding history:', error);
        this.auditByCode[code] = [];
        this.auditLoading = null;
      }
    );
  }

  /** The verbatim quote is the whole point of the evidence gate — show it, not a summary. */
  quotesFor(suggestion: any): any[] {
    return Array.isArray(suggestion?.evidence) ? suggestion.evidence : [];
  }

  /**
   * True when the evidence is an AI description of an image rather than a transcribed report.
   *
   * Including these was a deliberate decision; distinguishing them is the mitigation. A
   * pathology report states a diagnosis a pathologist made. An 'MRI Image' description is a
   * model's reading of a picture, so a code resting on it stacks two AI judgements with no
   * human in between.
   */
  isInferredSource(source: string): boolean {
    return /MRI Image|Patient Image|Other/i.test(source || '');
  }

  actionLabel(action: string): string {
    switch (action) {
      case 'add': return 'Add';
      case 'remove': return 'Remove';
      case 'replace': return 'Replace';
      case 'reclassify': return 'Change role';
      default: return action;
    }
  }

  roleLabel(role: string | null): string {
    switch (role) {
      case 'primary': return 'Primary';
      case 'secondary': return 'Secondary';
      case 'comorbidity': return 'Comorbidity';
      case 'complication': return 'Complication';
      case 'external-cause': return 'External cause';
      case 'morphology': return 'Morphology';
      default: return '';
    }
  }

  stateLabel(row: any): string {
    switch (row.state) {
      case 'adding': return 'New';
      case 'removing': return 'Removing';
      case 'choice': return 'Alternative';
      case 'role-change': return `${this.roleLabel(row.role) || 'Unset'} → ${this.roleLabel(row.newRole)}`;
      default: return 'On record';
    }
  }

  /** What the clinician is being asked, in one line, for the header of a decision card. */
  decisionTitle(decision: any): string {
    if (decision.kind === 'together') {
      return `These ${decision.members.length} codes are one diagnosis — they are accepted or dismissed together`;
    }
    if (decision.kind === 'exclusive') {
      return 'Both describe the same condition. Choose the one the notes support.';
    }
    return '';
  }

  verdictLabel(verdict: string | null): string {
    switch (verdict) {
      case 'agree': return 'Second check agreed';
      case 'disagree': return 'Second check disagreed';
      case 'unsure': return 'Second check was unsure';
      case 'abstain': return 'Second check abstained';
      default: return 'Not independently checked';
    }
  }

  close(): void {
    // Optional: when embedded in the review wizard there is no dialog to close, and the
    // wizard owns the closing. `changed` is read from the outside in that case.
    this.dialogRef?.close({ changed: this.changed });
  }
}
